import { gql } from '@apollo/client';
import _ from 'lodash';

import { getLastFromMinLengthArray } from 'src/lib/getLastFromMinLengthArray';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const SCHEMA_NAME = 'schema definition';

export const FlatDiffItemFragment = gql`
  fragment FlatDiffItemFragment on FlatDiffItem {
    ... on FlatDiffItemCoordinate {
      coordinate
    }
    ... on FlatDiffItemRootType {
      rootType
    }
  }
`;

// Type
// Type.field
// @Directive
// Type.field(arg:)
// @Directive(arg:)
export type Coordinate =
  | { directive: string; arg?: string }
  | { type: string; field?: string; arg?: string }
  | { schemaDefinition: boolean; schemaExtension: boolean; field?: string };
export function parseCoordinate(
  diffItem: GraphQLTypes.FlatDiffItemFragment,
): Coordinate {
  if ('coordinate' in diffItem) {
    const coordinate = diffItem.coordinate;
    const [typeAndArg, fieldAndArg = ''] = coordinate.split('.');
    const [type, possibleDirectiveArg = ''] = typeAndArg.split('(');
    const [field, possibleFieldArg = ''] = fieldAndArg.split('(');
    const arg =
      possibleDirectiveArg.replace(':)', '') ||
      possibleFieldArg.replace(':)', '');
    if (!type) {
      throw new Error('invalid coordinate');
    } else if (type.startsWith('@')) {
      return {
        directive: type.substring(1),
        arg: arg || undefined,
      };
    } else {
      return {
        type,
        field: field || undefined,
        arg: arg || undefined,
      };
    }
  } else {
    const isRootType = 'rootType' in diffItem;
    const isDescription = diffItem.__typename.includes('Description');
    const isDirectiveUsage = diffItem.__typename.includes('DirectiveUsage');
    // Root types and directive diff items are not specific to schema definitions or extensions
    // so we need to check if they are part of a schema definition or extension
    const isRootTypeOrDirectiveUsageChange = isRootType || isDirectiveUsage;
    const isSchemaDefinition =
      diffItem.__typename.includes('Definition') ||
      isDescription ||
      isRootTypeOrDirectiveUsageChange;

    return {
      schemaDefinition: isSchemaDefinition,
      schemaExtension: isRootTypeOrDirectiveUsageChange,
      field: isRootType ? diffItem.rootType : undefined,
    };
  }
}

export function groupDiffItems(diff: GraphQLTypes.FlatDiffItemFragment[]) {
  return diff.reduce(
    (
      acc: Record<
        string,
        MinLengthArray<1, GraphQLTypes.FlatDiffItemFragment[]>
      >,
      diffItem,
    ) => {
      const coordinate = parseCoordinate(diffItem);
      const baseType =
        'schemaDefintion' in coordinate || 'schemaExtension' in coordinate
          ? SCHEMA_NAME
          : 'type' in coordinate
            ? coordinate.type
            : coordinate.directive;
      const accDiff = getOrSetRecord(baseType, [[]], acc);

      const lastTypenameForType = _.last(
        getLastFromMinLengthArray(accDiff),
      )?.__typename;
      if (
        lastTypenameForType &&
        [
          'FlatDiffRemoveDirective',
          'FlatDiffRemoveEnum',
          'FlatDiffRemoveInput',
          'FlatDiffRemoveInterface',
          'FlatDiffRemoveObject',
          'FlatDiffRemoveScalar',
          'FlatDiffRemoveUnion',
        ].includes(lastTypenameForType)
      ) {
        accDiff.push([]);
      }
      getLastFromMinLengthArray(accDiff).push(diffItem);
      return acc;
    },
    {},
  );
}
